// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

// ── How much of a project was actually recorded ──────────────────────────────
//
// The buyout log is filled in by hand, after the fact, from numbers that
// usually live in an ERP. Nobody completes it. The rows that do get typed are
// the memorable ones, and what makes a buyout memorable is that it went badly.
//
// So every figure derived from this log is computed on a self-selected sample,
// and the selection is biased in a known direction. That is survivable while
// the figure stays inside one project -- the estimator can see their own job
// and knows whether fourteen of thirty-eight scopes is the whole story.
// It is not survivable the moment the number is generalised, because then
// nobody can see what it was computed from.
//
// This module exists to put the denominator on screen. A variance quoted
// without it is a verdict; the same variance quoted as "on 62% of the value
// you have recorded" is an observation the reader can weigh.

import type { BuyoutItem } from "../types";
import { isAwarded } from "./buyoutStatus";

export type BuyoutCoverage = {
  /** Rows carrying both a budget and an awarded contract amount. */
  recorded: number;
  /** Rows on the log at all. */
  total: number;
  /** Budgeted value of the recorded rows. */
  recordedValue: number;
  /** Budgeted value of every row on the log. */
  totalValue: number;
  /** recordedValue / totalValue, 0 when there is nothing to divide. */
  valuePct: number;
};

/**
 * Coverage by value rather than by row count.
 *
 * Ten small scopes recorded out of twelve looks like 83% and means little if
 * the two missing ones are the concrete and the steel. Dollars are what the
 * variance is weighted by, so dollars are what the coverage has to describe.
 */
export function buyoutCoverage(rows: BuyoutItem[] | null | undefined): BuyoutCoverage {
  let recorded = 0, total = 0, recordedValue = 0, totalValue = 0;
  for (const r of rows || []) {
    const budget = Number(r.budgetAmount) || 0;
    const contract = Number(r.contractAmount) || 0;
    total++;
    totalValue += budget;
    if (budget > 0 && contract > 0 && isAwarded(r.status || "")) {
      recorded++;
      recordedValue += budget;
    }
  }
  return {
    recorded, total, recordedValue, totalValue,
    valuePct: totalValue > 0 ? recordedValue / totalValue : 0,
  };
}

/** One line for the top of the log, or null when the log is empty. */
export function coverageNote(c: BuyoutCoverage): string | null {
  if (c.total === 0) return null;
  const pct = Math.round(c.valuePct * 100);
  return `${c.recorded} of ${c.total} scope${c.total === 1 ? "" : "s"} recorded · ${pct}% of budgeted value`;
}
